import React, { useState, useRef, useEffect } from 'react';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { FunnelIcon as FunnelIconSolid } from '@heroicons/react/24/solid';
import { clsx } from 'clsx';
import { L2CP_COLUMN_IDS, L2CP_GROUP_ID } from '@/stores';
import { PinLeftIcon, PinRightIcon } from '@/components/ui/icons/PinIcons';

type ColumnHeaderProps = {
  columnId: string;
  headerText: string | React.ReactNode;
  headerTooltip?: string;
  width: number;
  sortState?: { id: string; desc: boolean };
  /** False when the table can't sort this column: no chevron, header not clickable */
  sortable?: boolean;
  onSort: (columnId: string) => void;
  isLoading: boolean;
  hasError: boolean;
  hoveredColumn: string | null;
  onHover: (columnId: string | null) => void;
  align?: 'left' | 'right';
  /** When true, button shrinks to content so sort icon stays next to text (e.g. in L2CP group) */
  shrinkToContent?: boolean;
  /** Position of sort icon: 'left' (before label) or 'right' (after label). Default follows align. */
  sortIconPosition?: 'left' | 'right';
  /** Show filter icon; click opens filter panel and focuses this column's filter */
  filterable?: boolean;
  onFilterClick?: (columnId: string) => void;
  hasActiveFilter?: boolean;
  /** When true, render filter icon after the label (e.g. between text and expand button for L2CP) */
  filterIconAfterText?: boolean;
  /** When true, hide the filter icon; the sort chevron stays (LR-669) */
  hideControls?: boolean;
  /** Optional drag handle element rendered directly left of the header text */
  dragHandle?: React.ReactNode;
  /** Pin side indicator — when set, shows a small pin icon in the header */
  pinSide?: 'left' | 'right' | null;
  /** Called when the pin icon is clicked — should unpin the column */
  onUnpin?: () => void;
};

export const ColumnHeader: React.FC<ColumnHeaderProps> = ({
  columnId,
  headerText,
  headerTooltip,
  width,
  sortState,
  sortable = true,
  onSort,
  isLoading,
  hasError,
  hoveredColumn,
  onHover,
  align = 'left',
  shrinkToContent = false,
  sortIconPosition,
  filterable = false,
  onFilterClick,
  hasActiveFilter = false,
  filterIconAfterText = false,
  hideControls = false,
  dragHandle,
  pinSide = null,
  onUnpin,
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const isSortable = sortable && columnId !== 'rowNumber';
  const headerString =
    typeof headerText === 'string' ? headerText : (headerTooltip ?? '');
  const iconLeft =
    sortIconPosition === 'left' ||
    (sortIconPosition === undefined && align === 'right');
  const iconRight =
    sortIconPosition === 'right' ||
    (sortIconPosition === undefined && align === 'left');
  const showLeftIconOnlyWhenSorted = sortIconPosition === 'left';
  const sortIcon = (
    <span
      className={`flex flex-shrink-0 items-center justify-center self-center ${iconLeft ? 'mr-1' : ''}`}
    >
      {isSortable && sortState ? (
        sortState.desc ? (
          <ChevronDownIcon className="h-3 w-3 text-gray-400" />
        ) : (
          <ChevronUpIcon className="h-3 w-3 text-gray-400" />
        )
      ) : (
        <div className="h-3 w-3" />
      )}
    </span>
  );
  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const el = textRef.current;
        setIsTruncated(
          el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight
        );
      }
    };
    checkTruncation();
  }, [headerString, width]);
  const handleFilterClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    onFilterClick?.(columnId);
  };

  const handleFilterKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.stopPropagation();
      e.preventDefault();
      onFilterClick?.(columnId);
    }
  };

  const filterButton =
    filterable && onFilterClick && !hideControls ? (
      <span
        role="button"
        tabIndex={0}
        onClick={handleFilterClick}
        onKeyDown={handleFilterKeyDown}
        className={clsx(
          'inline-flex flex-shrink-0 cursor-pointer rounded p-0.5 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
          hasActiveFilter
            ? 'text-blue-600 opacity-100'
            : 'text-gray-400 opacity-0 hover:text-gray-600 hover:opacity-100 group-hover:opacity-100'
        )}
        title="Open filter"
        aria-label="Open filter for column"
        aria-pressed={hasActiveFilter}
      >
        {hasActiveFilter ? (
          <FunnelIconSolid className="h-3.5 w-3.5" />
        ) : (
          <FunnelIcon className="h-3.5 w-3.5" />
        )}
      </span>
    ) : null;

  const filterSlot =
    filterable && onFilterClick && !hideControls ? (
      <span
        className={clsx(
          'inline-flex w-5 flex-shrink-0 items-center justify-center self-center',
          !hasActiveFilter &&
            'opacity-0 transition-opacity duration-150 group-hover:opacity-100'
        )}
      >
        {filterButton}
      </span>
    ) : null;

  const isL2cpInner =
    columnId !== L2CP_GROUP_ID &&
    L2CP_COLUMN_IDS.includes(columnId as (typeof L2CP_COLUMN_IDS)[number]);

  const labelClassName = clsx(
    'flex min-w-0 flex-1 items-center py-1 transition-colors',
    align === 'right' ? 'justify-end' : 'text-left',
    align === 'left' && 'mr-1.5',
    shrinkToContent && 'w-auto flex-none'
  );
  const label = (
    <>
      {align !== 'right' && dragHandle && (
        <span className="mr-1">{dragHandle}</span>
      )}
      {iconLeft &&
        (showLeftIconOnlyWhenSorted
          ? sortState
            ? sortIcon
            : null
          : sortIcon)}
      {iconLeft && !filterIconAfterText && filterSlot}
      <span
        ref={textRef}
        className={clsx(
          'min-w-0 overflow-hidden text-ellipsis whitespace-nowrap leading-tight',
          typeof headerText !== 'string' && 'block min-h-[2.5rem]',
          align === 'right' ? 'text-right' : iconRight ? 'mr-1' : ''
        )}
      >
        {headerText}
      </span>
      {filterIconAfterText && filterSlot}
      {iconRight && !filterIconAfterText && filterSlot}
      {iconRight && sortIcon}
    </>
  );

  return (
    <div
      className={`group relative flex min-w-0 flex-1 items-center ${isL2cpInner ? 'justify-end' : ''}`}
      onMouseEnter={() => onHover(columnId)}
      onMouseLeave={() => onHover(null)}
    >
      {sortable ? (
        <button
          onClick={() => onSort(columnId)}
          disabled={isLoading || hasError || !isSortable}
          className={clsx(
            labelClassName,
            !isSortable ? 'cursor-pointer' : 'disabled:cursor-not-allowed'
          )}
        >
          {label}
        </button>
      ) : (
        // Not a disabled button: Chromium drops a click on the filter icon inside one.
        <div className={labelClassName}>{label}</div>
      )}
      {pinSide && onUnpin && (
        <span className="ml-0.5 inline-flex w-5 flex-shrink-0 items-center justify-center self-center">
          <button
            type="button"
            className="flex items-center rounded p-0.5 text-blue-600 transition-colors hover:text-blue-800"
            onClick={(e) => {
              e.stopPropagation();
              onUnpin();
            }}
            title="Unpin column"
          >
            {pinSide === 'left' ? (
              <PinLeftIcon className="h-3.5 w-3.5" />
            ) : (
              <PinRightIcon className="h-3.5 w-3.5" />
            )}
          </button>
        </span>
      )}
      {align === 'right' && dragHandle && (
        <span className="ml-1">{dragHandle}</span>
      )}
      {hoveredColumn === columnId && isTruncated && (
        <div className="pointer-events-none absolute right-0 top-full z-50 mt-1 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white">
          {typeof headerText === 'string'
            ? headerText
            : (headerTooltip ?? headerString) || 'Column'}
          <div className="absolute bottom-full left-1/2 h-0 w-0 -translate-x-1/2 border-b-4 border-l-4 border-r-4 border-transparent border-b-gray-900" />
        </div>
      )}
    </div>
  );
};
